import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { LocalHospital as Metro, Place as Remote } from "@mui/icons-material";

import { ClearCallLogoTitle } from "../../../components/clear-call-logo";
import { MeetingLayout } from "../../../types";

interface MeetingLayoutSelectionPageProps {
  onSelectLayout: (layout: MeetingLayout) => void;
}

const layoutOptions: {
  key: MeetingLayout;
  title: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  background: string;
}[] = [
    {
      key: "metro",
      title: "Metro Layout",
      description:
        "Suitable for specialists and teams in centralised locations.",
      icon: <Metro sx={{ fontSize: 34 }} />,
      accent: "primary.main",
      background: "rgba(25, 118, 210, 0.08)",
    },
    {
      key: "remote",
      title: "Remote Layout",
      description:
        "Suitable for in-person meetings, when used in conjunction with a headset and microphones.",
      icon: <Remote sx={{ fontSize: 34 }} />,
      accent: "success.main",
      background: "rgba(46, 125, 50, 0.08)",
    },
  ];

const MeetingLayoutSelectionPage: React.FC<MeetingLayoutSelectionPageProps> = ({
  onSelectLayout,
}) => {
  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(0deg,rgb(222, 238, 253),rgb(172, 220, 226))",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          p: 2,
          pt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 2,
        }}
      >
        <ClearCallLogoTitle size={120} />

        <Stack
          spacing={3}
          sx={{
            mt: 3,
            width: "100%",
            maxWidth: 620,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#FAFAFA",
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={0.5} alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" textAlign="center">
              Choose Meeting Layout
            </Typography>

            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
            >
              Select the layout of the meeting user interface that best suits your
              needs:
            </Typography>
          </Stack>

          <Stack spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
            {layoutOptions.map((option) => (
              <Stack
                key={option.key}
                direction="row"
                alignItems="center"
                spacing={2.5}
                onClick={() => onSelectLayout(option.key)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectLayout(option.key);
                  }
                }}
                role="button"
                tabIndex={0}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  p: 3,
                  minHeight: 110,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 3,
                  backgroundColor: "#FFFFFF",
                  cursor: "pointer",
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.12)",
                    borderColor: option.accent,
                    transform: "translateY(-2px)",
                  },
                  "&:focus-visible": {
                    outline: 2,
                    outlineColor: option.accent,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 68,
                    height: 68,
                    borderRadius: 3,
                    backgroundColor: option.background,
                    color: option.accent,
                    flexShrink: 0,
                  }}
                >
                  {option.icon}
                </Box>

                <Stack
                  sx={{
                    flex: 1,
                    justifyContent: "center",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 600, wordBreak: "break-word" }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 0.5, wordBreak: "break-word" }}
                  >
                    {option.description}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default MeetingLayoutSelectionPage;
